import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { apiClient } from '@/api/client';
import { useAuthStore } from '@/store/auth.store';
import type {
  Vendor,
  VendorContact,
  VendorFilters,
  VendorListMeta,
  CreateVendorDto,
  PortalContact,
} from '@/api/types/vendor.types';
import type { Assessment, Certification, RiskAcceptanceSummary, VendorBiSummary } from '@/api/types/assessment.types';

// ── List ──────────────────────────────────────────────────────────────────────

export interface UseVendorsParams {
  filters?: VendorFilters;
  page?: number;
  perPage?: number;
  // RA-FU-6: optional enabled gate for typeahead pattern; defaults to true
  enabled?: boolean;
}

export function useVendors({ filters = {}, page = 1, perPage = 25, enabled: enabledProp }: UseVendorsParams = {}) {
  const { tenantId, isAuthenticated } = useAuthStore();

  const params = new URLSearchParams();
  params.set('page', String(page));
  params.set('per_page', String(perPage));
  if (filters.tier) params.set('tier', filters.tier);
  if (filters.status) params.set('status', filters.status);
  if (filters.search) params.set('search', filters.search);
  // Slice 3 — additive BI segment filters
  if (filters.tiers) params.set('tiers', filters.tiers);
  if (filters.overdue) params.set('overdue', filters.overdue);
  if (filters.has_findings) params.set('has_findings', filters.has_findings);
  if (filters.finding_severity) params.set('finding_severity', filters.finding_severity);
  if (filters.cert_expiring) params.set('cert_expiring', filters.cert_expiring);
  if (filters.ra_open) params.set('ra_open', filters.ra_open);
  if (filters.outcome) params.set('outcome', filters.outcome);
  if (filters.active_only) params.set('active_only', filters.active_only);

  return useQuery({
    queryKey: ['vendors', tenantId, { filters, page, perPage }],
    queryFn: async (): Promise<{ vendors: Vendor[]; meta: VendorListMeta }> => {
      const { data } = await apiClient.get<{
        data: Vendor[];
        meta: VendorListMeta;
      }>(`/vendors?${params.toString()}`);
      return { vendors: data.data ?? [], meta: data.meta };
    },
    enabled: isAuthenticated && (enabledProp !== false),
    staleTime: 60_000,
    placeholderData: (prev) => prev,
  });
}

// ── Population BI summary (Vendors Intelligence Workspace, Slice 1) ─────────────
// Tenant-wide aggregate; ACTIVE-ONLY KPIs/distributions + a range-scoped `vendors_added` delta.
// ⚠ No composite / avg-composite / external-rating (honest by construction — the endpoint omits them).

export type PortfolioBiRange = '30d' | '90d' | '6m' | '12m';

export interface PortfolioBiSummary {
  range: string;
  since: string;
  scope: { active_vendors: number; retired_vendors: number; total_on_file: number };
  kpis: {
    active_vendors: number;
    critical_high_tier: number;
    assessments_in_flight: number;
    assessments_overdue: number;
    open_findings: number;
    open_findings_critical: number;
    open_findings_vendors: number;
    certs_expiring_90d: number;
    certs_expired: number;
    risk_acceptances_open: number;
    risk_acceptances_expiring_60d: number;
  };
  deltas: { vendors_added: number };
  distributions: {
    by_tier: Record<string, number>;
    by_lifecycle: Record<string, number>;
    by_outcome: { pass: number; conditional: number; fail: number };
    by_severity: Record<string, number>;
  };
  trends: {
    granularity: string;
    buckets: string[];
    population: { total: number[]; added: number[]; retired: number[] };
    posture: { pass: number[]; conditional: number[]; fail: number[] };
    activity: { issued: number[]; submitted: number[]; completed: number[] };
    // Findings raised over time by severity (incl. the honest 'unspecified' band for NULL flag_severity).
    findings_severity: { critical: number[]; high: number[]; medium: number[]; low: number[]; informational: number[]; unspecified: number[] };
    findings_burndown: { available: boolean; since_note: string };
  };
  attention: PortfolioAttentionRow[];
}

export interface PortfolioAttentionRow {
  vendor_id: string;
  vendor_name: string;
  tier: string;
  signals: { type: 'overdue' | 'cert' | 'ra' | 'findings' | string; label: string; reason: string }[];
}

// Server portfolio export (Slice 4). ⚠ Summary is server-enforced aggregate-only + sanitizes the raw
// search term; the client just streams the file. apiClient carries the JWT (a plain <a href> would not).
export async function downloadPortfolioExport(
  scope: 'summary' | 'full',
  format: 'csv' | 'xlsx',
  currentParams: URLSearchParams,
  range: string,
): Promise<void> {
  const p = new URLSearchParams(currentParams);
  p.set('scope', scope);
  p.set('format', format);
  p.set('range', range);
  const res = await apiClient.get(`/vendors/portfolio-export?${p.toString()}`, { responseType: 'blob' });
  const blob = res.data as Blob;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  const cd = (res.headers?.['content-disposition'] as string | undefined) ?? '';
  const match = cd.match(/filename="?([^"]+)"?/);
  a.download = match?.[1] ?? `vendor-portfolio-${scope}.${format}`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export function usePortfolioBiSummary(range: PortfolioBiRange = '90d') {
  const { tenantId, isAuthenticated } = useAuthStore();
  return useQuery({
    queryKey: ['vendors-bi-summary', tenantId, range],
    queryFn: async (): Promise<PortfolioBiSummary> => {
      const { data } = await apiClient.get<{ data: PortfolioBiSummary }>(`/vendors/bi-summary?range=${range}`);
      return data.data;
    },
    enabled: isAuthenticated,
    staleTime: 60_000,
  });
}

// ── Single ────────────────────────────────────────────────────────────────────

export function useVendor(id: string | undefined) {
  const { tenantId, isAuthenticated } = useAuthStore();

  return useQuery({
    queryKey: ['vendor', tenantId, id],
    queryFn: async (): Promise<Vendor> => {
      const { data } = await apiClient.get<{ data: Vendor }>(`/vendors/${id}`);
      if (!data.data) throw new Error('Vendor not found');
      return data.data;
    },
    enabled: isAuthenticated && !!id,
    staleTime: 60_000,
  });
}

// ── Contacts ──────────────────────────────────────────────────────────────────

export function useVendorContacts(vendorId: string | undefined) {
  const { tenantId, isAuthenticated } = useAuthStore();

  return useQuery({
    queryKey: ['vendor-contacts', tenantId, vendorId],
    queryFn: async (): Promise<VendorContact[]> => {
      const { data } = await apiClient.get<{ data: VendorContact[] }>(
        `/vendors/${vendorId}/contacts`,
      );
      return data.data ?? [];
    },
    enabled: isAuthenticated && !!vendorId,
    staleTime: 60_000,
  });
}

// ── Assessments for a vendor ──────────────────────────────────────────────────

export function useVendorAssessments(vendorId: string | undefined) {
  const { tenantId, isAuthenticated } = useAuthStore();

  return useQuery({
    queryKey: ['vendor-assessments', tenantId, vendorId],
    queryFn: async (): Promise<Assessment[]> => {
      const { data } = await apiClient.get<{ data: Assessment[] }>(
        `/assessments?vendor_id=${vendorId}&per_page=50`,
      );
      return data.data ?? [];
    },
    enabled: isAuthenticated && !!vendorId,
    staleTime: 60_000,
  });
}

// ── Certifications ────────────────────────────────────────────────────────────

export function useVendorCertifications(vendorId: string | undefined) {
  const { tenantId, isAuthenticated } = useAuthStore();

  return useQuery({
    queryKey: ['vendor-certifications', tenantId, vendorId],
    queryFn: async (): Promise<Certification[]> => {
      const { data } = await apiClient.get<{ data: Certification[] }>(
        `/vendors/${vendorId}/certifications`,
      );
      return data.data ?? [];
    },
    enabled: isAuthenticated && !!vendorId,
    staleTime: 60_000,
  });
}

// ── Risk Acceptances ──────────────────────────────────────────────────────────

export function useVendorRiskAcceptances(vendorId: string | undefined) {
  const { tenantId, isAuthenticated } = useAuthStore();

  return useQuery({
    queryKey: ['vendor-risk-acceptances', tenantId, vendorId],
    queryFn: async (): Promise<RiskAcceptanceSummary[]> => {
      const { data } = await apiClient.get<{ data: RiskAcceptanceSummary[] }>(
        `/vendors/${vendorId}/risk-acceptances`,
      );
      return data.data ?? [];
    },
    enabled: isAuthenticated && !!vendorId,
    staleTime: 60_000,
  });
}

export function useCreateRiskAcceptance(vendorId: string) {
  const qc = useQueryClient();
  const { tenantId } = useAuthStore();
  return useMutation({
    mutationFn: async (dto: {
      title: string;
      description: string;
      risk_category: 'vendor_risk' | 'compliance_gap' | 'vulnerability' | 'operational_risk' | 'other';
      compensating_controls?: string;
      business_justification: string;
      assessment_id?: string;
      response_id?: string;
      acceptance_expires_at?: string;
    }) => {
      const { data } = await apiClient.post(`/vendors/${vendorId}/risk-acceptances`, dto);
      return data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['vendor-risk-acceptances', tenantId, vendorId] });
    },
  });
}

// ── Portal Contacts ───────────────────────────────────────────────────────────

export function usePortalContacts(vendorId: string | undefined) {
  const { tenantId, isAuthenticated } = useAuthStore();

  return useQuery({
    queryKey: ['portal-contacts', tenantId, vendorId],
    queryFn: async (): Promise<PortalContact[]> => {
      const { data } = await apiClient.get<{ data: PortalContact[] }>(
        `/vendors/${vendorId}/portal-contacts`,
      );
      return data.data ?? [];
    },
    enabled: isAuthenticated && !!vendorId,
    staleTime: 30_000,
  });
}

export function useInvitePortalContact(vendorId: string) {
  const queryClient = useQueryClient();
  const { tenantId } = useAuthStore();

  return useMutation({
    mutationFn: async (dto: {
      email: string;
      firstName: string;
      lastName: string;
      roleTitle?: string;
    }): Promise<PortalContact> => {
      const { data } = await apiClient.post<{ data: PortalContact }>(
        `/vendors/${vendorId}/portal-contacts/invite`,
        dto,
      );
      if (!data.data) throw new Error('Failed to invite contact');
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['portal-contacts', tenantId, vendorId] });
      toast.success('Invitation sent');
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.errors?.message ?? err.message ?? 'Failed to send invitation';
      toast.error(msg);
    },
  });
}

export function useRemovePortalContact(vendorId: string) {
  const queryClient = useQueryClient();
  const { tenantId } = useAuthStore();

  return useMutation({
    mutationFn: async (contactId: string): Promise<void> => {
      await apiClient.delete(`/vendors/${vendorId}/portal-contacts/${contactId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['portal-contacts', tenantId, vendorId] });
      toast.success('Contact removed');
    },
    onError: (err: Error) => {
      toast.error(err.message || 'Failed to remove contact');
    },
  });
}

export function useResendPortalInvite(vendorId: string) {
  return useMutation({
    mutationFn: async (contactId: string): Promise<void> => {
      await apiClient.post(`/vendors/${vendorId}/portal-contacts/${contactId}/resend`);
    },
    onSuccess: (_data, _contactId, _ctx) => {
      toast.success('Invitation resent');
    },
    onError: (err: Error) => {
      toast.error(err.message || 'Failed to resend invitation');
    },
  });
}

export function useDisablePortalContact(vendorId: string) {
  const queryClient = useQueryClient();
  const { tenantId } = useAuthStore();

  return useMutation({
    mutationFn: async (contactId: string): Promise<void> => {
      await apiClient.post(`/vendors/${vendorId}/portal-contacts/${contactId}/disable`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['portal-contacts', tenantId, vendorId] });
      toast.success('Contact disabled');
    },
    onError: (err: Error) => {
      toast.error(err.message || 'Failed to disable contact');
    },
  });
}

export function useEnablePortalContact(vendorId: string) {
  const queryClient = useQueryClient();
  const { tenantId } = useAuthStore();

  return useMutation({
    mutationFn: async (contactId: string): Promise<void> => {
      await apiClient.post(`/vendors/${vendorId}/portal-contacts/${contactId}/enable`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['portal-contacts', tenantId, vendorId] });
      toast.success('Contact enabled');
    },
    onError: (err: Error) => {
      toast.error(err.message || 'Failed to enable contact');
    },
  });
}

// ── Key Contact CRUD ──────────────────────────────────────────────────────────

export function useCreateContact(vendorId: string) {
  const queryClient = useQueryClient();
  const { tenantId } = useAuthStore();

  return useMutation({
    mutationFn: async (dto: { firstName: string; lastName: string; email: string; title?: string }): Promise<VendorContact> => {
      const { data } = await apiClient.post<{ data: VendorContact }>(`/vendors/${vendorId}/contacts`, dto);
      if (!data.data) throw new Error('Failed to create contact');
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendor-contacts', tenantId, vendorId] });
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.errors?.message ?? err.message ?? 'Failed to add contact';
      toast.error(msg);
    },
  });
}

export function useUpdateContact(vendorId: string) {
  const queryClient = useQueryClient();
  const { tenantId } = useAuthStore();

  return useMutation({
    mutationFn: async (dto: { contactId: string; firstName?: string; lastName?: string; email?: string; title?: string }): Promise<VendorContact> => {
      const { contactId, ...body } = dto;
      const { data } = await apiClient.patch<{ data: VendorContact }>(`/vendors/${vendorId}/contacts/${contactId}`, body);
      if (!data.data) throw new Error('Failed to update contact');
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendor-contacts', tenantId, vendorId] });
    },
    onError: (err: Error) => {
      toast.error(err.message || 'Failed to update contact');
    },
  });
}

export function useDeleteContact(vendorId: string) {
  const queryClient = useQueryClient();
  const { tenantId } = useAuthStore();

  return useMutation({
    mutationFn: async (contactId: string): Promise<void> => {
      await apiClient.delete(`/vendors/${vendorId}/contacts/${contactId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendor-contacts', tenantId, vendorId] });
    },
    onError: (err: Error) => {
      toast.error(err.message || 'Failed to remove contact');
    },
  });
}

// ── Update ────────────────────────────────────────────────────────────────────

export function useUpdateVendor(vendorId: string) {
  const queryClient = useQueryClient();
  const { tenantId } = useAuthStore();

  return useMutation({
    mutationFn: async (dto: Partial<{
      name: string;
      tier: string;
      industry: string;
      website: string;
      country: string;
      description: string;
      contract_start_date: string;
      contract_end_date: string;
      data_access_level: string;
      services_provided: string[];
    }>): Promise<Vendor> => {
      const { data } = await apiClient.patch<{ data: Vendor }>(`/vendors/${vendorId}`, dto);
      if (!data.data) throw new Error('Failed to update vendor');
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendor', tenantId, vendorId] });
      toast.success('Vendor profile updated');
    },
    onError: (err: Error) => {
      toast.error(err.message || 'Failed to update vendor');
    },
  });
}

// ── Vendor Cert Status (CLDS Sub-sprint 2 §5.6.1) ────────────────────────────

export type VendorCertStatus =
  | 'no_certification'
  | 'valid_certification'
  | 'expiring_soon'
  | 'expired_certification'
  | 'withdrawn_certification'
  | 'multiple_certifications';

export interface VendorCertStatusDetail {
  active_count: number;
  expiring_count: number;
  expired_count: number;
  withdrawn_count: number;
}

export interface VendorCertStatusResult {
  status: VendorCertStatus;
  detail: VendorCertStatusDetail;
}

export function useVendorBiSummary(vendorId: string | undefined) {
  const { tenantId, isAuthenticated } = useAuthStore();
  return useQuery({
    queryKey: ['vendor-bi-summary', tenantId, vendorId],
    queryFn: async (): Promise<VendorBiSummary> => {
      const { data } = await apiClient.get<{ data: VendorBiSummary }>(`/vendors/${vendorId}/bi-summary`);
      return data.data;
    },
    enabled: isAuthenticated && !!vendorId,
    staleTime: 60_000,
  });
}

export function useVendorCertStatus(vendorId: string | undefined) {
  const { tenantId, isAuthenticated } = useAuthStore();

  return useQuery({
    queryKey: ['vendor-cert-status', tenantId, vendorId],
    queryFn: async (): Promise<VendorCertStatusResult> => {
      const { data } = await apiClient.get<{ data: VendorCertStatusResult }>(
        `/vendors/${vendorId}/cert-status`,
      );
      return data.data;
    },
    enabled: isAuthenticated && !!vendorId,
    staleTime: 60_000,
  });
}

// ── Cert review action (CLDS Sub-sprint 3) ───────────────────────────────────

export function useReviewCertification(vendorId: string) {
  const queryClient = useQueryClient();
  const { tenantId } = useAuthStore();

  return useMutation({
    mutationFn: async (dto: {
      certId: string;
      action: 'accepted' | 'needs_follow_up' | 'rejected';
      reason: string | null;
    }): Promise<{ certId: string; reviewStatus: string }> => {
      const { data } = await apiClient.post<{ data: { certId: string; reviewStatus: string } }>(
        `/tenant/vendors/${vendorId}/certifications/${dto.certId}/review`,
        { action: dto.action, reason: dto.reason },
      );
      if (!data.data) throw new Error('Review failed');
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendor-certifications', tenantId, vendorId] });
      queryClient.invalidateQueries({ queryKey: ['vendor-cert-status', tenantId, vendorId] });
      toast.success('Review submitted');
    },
    onError: (err: any) => {
      const code = err?.response?.data?.errors?.[0]?.code;
      if (code === 'CERT_ARCHIVED') {
        toast.error('This certification has been withdrawn by the vendor');
      } else {
        const msg = err?.response?.data?.errors?.[0]?.message ?? err.message ?? 'Failed to submit review';
        toast.error(msg);
      }
    },
  });
}

// ── Vendor Lifecycle Actions (VENDOR-LIFECYCLE.2) ────────────────────────────

export interface VendorLifecycleEvent {
  event_type: 'vendor.retired' | 'vendor.reactivated';
  occurred_at: string;
  actor_user_id: string;
  before_state: Record<string, unknown>;
  after_state: Record<string, unknown>;
  metadata: { reason: string; [key: string]: unknown };
}

export function useVendorLifecycleEvents(vendorId: string | undefined) {
  const { tenantId, isAuthenticated } = useAuthStore();

  return useQuery({
    queryKey: ['vendor-lifecycle-events', tenantId, vendorId],
    queryFn: async (): Promise<VendorLifecycleEvent[]> => {
      const { data } = await apiClient.get<{ data: VendorLifecycleEvent[] }>(
        `/vendors/${vendorId}/lifecycle-events`,
      );
      return data.data ?? [];
    },
    enabled: isAuthenticated && !!vendorId,
    staleTime: 30_000,
  });
}

export function useRetireVendor(vendorId: string) {
  const queryClient = useQueryClient();
  const { tenantId } = useAuthStore();

  return useMutation({
    mutationFn: async (dto: { reason: string }): Promise<Vendor> => {
      const { data } = await apiClient.post<{ data: Vendor }>(`/vendors/${vendorId}/retire`, dto);
      if (!data.data) throw new Error('Failed to retire vendor');
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendor', tenantId, vendorId] });
      queryClient.invalidateQueries({ queryKey: ['vendors', tenantId] });
      queryClient.invalidateQueries({ queryKey: ['vendor-lifecycle-events', tenantId, vendorId] });
      toast.success('Vendor retired');
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.errors?.message ?? err.message ?? 'Failed to retire vendor';
      toast.error(msg);
    },
  });
}

export function useReactivateVendor(vendorId: string) {
  const queryClient = useQueryClient();
  const { tenantId } = useAuthStore();

  return useMutation({
    mutationFn: async (dto: { reason: string }): Promise<Vendor> => {
      const { data } = await apiClient.post<{ data: Vendor }>(`/vendors/${vendorId}/reactivate`, dto);
      if (!data.data) throw new Error('Failed to reactivate vendor');
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendor', tenantId, vendorId] });
      queryClient.invalidateQueries({ queryKey: ['vendors', tenantId] });
      queryClient.invalidateQueries({ queryKey: ['vendor-lifecycle-events', tenantId, vendorId] });
      toast.success('Vendor reactivated');
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.errors?.message ?? err.message ?? 'Failed to reactivate vendor';
      toast.error(msg);
    },
  });
}

// ── Create ────────────────────────────────────────────────────────────────────

export function useCreateVendor() {
  const queryClient = useQueryClient();
  const { tenantId } = useAuthStore();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: async (dto: CreateVendorDto): Promise<Vendor> => {
      const { data } = await apiClient.post<{ data: Vendor }>('/vendors', dto);
      if (!data.data) throw new Error('Failed to create vendor');
      return data.data;
    },
    onSuccess: (vendor) => {
      queryClient.invalidateQueries({ queryKey: ['vendors', tenantId] });
      toast.success(`${vendor.name} added successfully`);
      navigate(`/vendors/${vendor.id}`);
    },
    onError: (err: Error) => {
      toast.error(err.message || 'Failed to create vendor');
    },
  });
}
